import { Circle as OLCircle, Fill as OLFill, Icon as OLIcon, Stroke as OLStroke, Style as OLStyle, Text as OLText } from 'ol/style.js';
import dateTimeConverter from '@/Utils/dateTimeConverter';
import resourceHelper from '@/Utils/resourceHelper';

/**
 * Helper to handle styling of elements on the map
 */
class MapStylingHelper {
  /**
   * Create a draggable marker for the given data type
   * @param {string} dataType - The type of data for the marker (e.g., 'business', 'camera', 'group').
   * @return {OLStyle} The OpenLayers style for the draggable marker.
   */
  createDraggableMarkerStyle(dataType: string): OLStyle {
    if (dataType === 'business') {
      return this.createImageMarkerStyle('draggableBusinessMarker.png');
    }

    if (dataType === 'camera') {
      return this.createImageMarkerStyle('draggableCameraMarker.png');
    }

    if (dataType === 'group') {
      return this.createImageMarkerStyle('draggableGroupMarker.png');
    }

    throw new Error(`The dataType of ${dataType} is not supported.`);
  }

  /**
   * Create a point marker containing the given text
   * @param {string} text - The text to display on the marker.
   * @return {OLStyle} The OpenLayers style for the point marker.
   */
  createPointMarkerStyle(text: string): OLStyle {
    return new OLStyle({
      image: new OLCircle({
        radius: 10,
        stroke: new OLStroke({
          color: '#575554',
          width: 2
        }),
        fill: new OLFill({
          color: '#ffffff'
        })
      }),
      text: new OLText({
        textAlign: 'start',
        textBaseline: 'middle',
        offsetX: 15,
        offsetY: 0,
        font: '16px RobotoRegular, Helvetica, Arial, sans-serif',
        fill: new OLFill({ color: '#222' }),
        stroke: new OLStroke({
          color: '#fff',
          width: 2
        }),
        text: text
      })
    });
  }

  getPopupContent(item: any, dataType: string): string {
    if (dataType === 'business') {
      return this.createBusinessPopupContent(item);
    }
    return this.createCameraPopupContent(item);
  }

  private createCameraPopupContent(camera: any): string {
    const date = dateTimeConverter.getMomentDate(camera.addedDateTime);
    return `<a href="#cameras/${camera.cameraId}"><strong>${camera.cameraName}</strong></a><br />` +
      `<div class="heading">${resourceHelper.getString('Added')}</div>` +
      date.format('llll');
  }

  private createBusinessPopupContent(business: any): string {
    return `<strong>${business.name}</strong><br />` + business.address;
  }

  private createImageMarkerStyle(imageFileName: string): OLStyle {
    return new OLStyle({
      image: new OLIcon({
        anchor: [0.5, 1.0],
        anchorXUnits: 'fraction',
        anchorYUnits: 'fraction',
        opacity: 0.75,
        src: 'Content/images/' + imageFileName
      })
    });
  }
}

export default new MapStylingHelper();
